import { isAfter } from "date-fns";
import { captureBreadcrumb } from "extensions/sentry";
import { debounce, noop } from "lodash-es";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { store } from "common/store";
import { request } from "common/components/request";
import { emitter } from "common/emitter";

import { RESET_APP_AUTH } from "common/actions/authentication";
import { HIDE_SESSION_TIMEOUT, INTERACT, INTERACT_DEBOUNCE_WAIT_MS } from "common/reducers/authentication";

export const INACTIVITY_MINS = 15;
export const TIMEOUT_WARNING_MINS = 2;

const isAuthenticated = () => isAfter(store.getState().authentication.exp * 1000, new Date());

export function useAuth({ trackInteraction = false } = {}) {
    // exp is in seconds
    // https://pyjwt.readthedocs.io/en/stable/usage.html#expiration-time-claim-exp
    const expiresAt = useSelector((state) => state.authentication.exp * 1000);
    const access_token = useSelector((state) => state.authentication.access_token);
    const sessionTimeoutModalShown = useSelector((state) => state.authentication.sessionTimeoutModal);
    
    const sso_user = useSelector((state) => state.common.user.sso_user);
    const api_host = useSelector((state) => state.config.api_host);
    const sso_provider = useSelector((state) => state.common.user.sso_provider);
    
    const dispatch = useDispatch();
    
    useEffect(() => {
        if (!trackInteraction) {
            return noop;
        }
        
        if (!isAuthenticated()) {
            // Whenever the user does have to sign-in again, wipe localStorage reducer of old values.
            // This will clean up all mimic attributes as well and destroy active user instance if it's still alive
            dispatch(RESET_APP_AUTH());
        }
    }, [dispatch, trackInteraction]);
    
    useEffect(() => {
        if (!trackInteraction) {
            return noop;
        }
        
        // Dispatch interact event right after the load
        dispatch(INTERACT({
            type: "load",
            debounced: 0
        }));
    }, [dispatch, trackInteraction]);
    
    useEffect(() => {
        if (!trackInteraction) {
            return noop;
        }
        
        const handleInteraction = debounce((event) => {
            // Do not track when session timeout window is open. It does not count as interaction apparently 🤷‍♂️
            if (sessionTimeoutModalShown) return;
            
            // We do not track anything without target
            if (!event.target) return;
            
            dispatch(INTERACT({
                type: event.type
            }));
        }, INTERACT_DEBOUNCE_WAIT_MS);
        
        document.addEventListener("click", handleInteraction);
        document.addEventListener("keypress", handleInteraction);
        document.addEventListener("scroll", handleInteraction);
        
        return () => {
            document.removeEventListener("click", handleInteraction);
            document.removeEventListener("keypress", handleInteraction);
            document.removeEventListener("scroll", handleInteraction);
        };
    }, [dispatch, trackInteraction, sessionTimeoutModalShown]);
    
    const logout = useCallback(async({ timeout = false } = {}) => {
        let logoutUrl = "/merchant/login";
        if (timeout) {
            logoutUrl = `${logoutUrl}?timeout=true`;
        }
        
        if (sso_user && sso_provider) {
            // Logout URL depends on SSO vs non-SSO
            logoutUrl = `${api_host}/saml/${sso_provider}/logout`;
        }
    
        captureBreadcrumb({
            message: "Performing logout",
            category: "security",
            data: {
                timeout,
                sso_user,
                sso_provider
            }
        });
        
        if (access_token && !sso_user) {
            try {
                await request("/user/logout", {
                    ensureAuth: false,
                
                    // API server needs token to mark it as inactive
                    headers: {
                        Authorization: `JWT ${access_token}`
                    }
                });
            } catch (error) {
                if (error.status !== 401) {
                    captureBreadcrumb({
                        message: "Failed logout request",
                        category: "security",
                        data: {
                            timeout,
                            sso_user,
                            sso_provider,
                            error
                        }
                    });
                }
            }
        }

        dispatch(HIDE_SESSION_TIMEOUT());
        dispatch(RESET_APP_AUTH({ afterLogoutUrl: logoutUrl }));
        emitter.emit("logout:complete");
    }, [dispatch, access_token, sso_user, api_host, sso_provider]);
    
    return {
        isAuthenticated,
        sessionTimeoutModalShown,
        logout,
        expiresAt
    };
}
